import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import type { Page } from '@/api/types';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag, Dot } from '@/components/Tag';
import { Empty } from '@/components/States';
import { date } from '@/lib/format';
import { AppliedChips, CardList, FilterButton, ListBody, ListScaffold, Pager, SearchBox, Segments, ViewChips } from '@/features/products/kit/List';
import { FilterSheet } from '@/features/products/kit/Sheet';
import { DataTable, Two, type Col } from '@/features/products/kit/Table';
import { useResource, useUrlState, withParam } from '@/features/products/kit/hooks';
import { SupplierCard } from './Cards';
import { SupplierDetail } from './SupplierDetail';
import { usePartnerSegments } from './nav';
import { activeState, settlementText } from './status';
import type { Supplier } from './types';

/**
 * 老接口 GET /api/suppliers 不支持关键词和筛选（只有分页，最多 500），老版也是一次取 200 条在前端搜。
 * 这里一次取 500 条，视图 / 类型 / 搜索 / 桌面分页都在前端做，条件照样写进 URL。
 */
const LIMIT = 500;
const PAGE = 30;
const VIEWS: { key: string; label: string; test: (v: Supplier) => boolean }[] = [
  { key: 'all', label: '全部供应商', test: () => true },
  { key: 'active', label: '启用', test: (v) => v.isActive },
  { key: 'inactive', label: '停用', test: (v) => !v.isActive },
  { key: 'monthly', label: '月结', test: (v) => v.isMonthlySettlement },
];

function matches(v: Supplier, q: string) {
  const k = q.toLocaleLowerCase('zh-CN');
  return [v.code, v.name, v.supplierType, v.contact, v.phone, v.address, v.note].some((x) => x?.toLocaleLowerCase('zh-CN').includes(k));
}

export function SuppliersPage() {
  const can = useSession((st) => st.can);
  if (!can('suppliers.read')) {
    return can('customers.read', 'taobao.manage') ? <Navigate to="/customers" replace /> : <Empty icon="lock" title="没有查看供应商的权限">需要「供应商 · 查看」权限，请联系管理员。</Empty>;
  }
  return <SupplierList />;
}

function SupplierList() {
  const ff = useFormFactor();
  const nav = useNavigate();
  const can = useSession((st) => st.can);
  const segments = usePartnerSegments();
  const [sp, set] = useUrlState();
  const view = VIEWS.find((v) => v.key === sp.get('view')) || VIEWS[0];
  const type = sp.get('type') || undefined;
  const q = sp.get('q') || '';
  const page = Math.max(1, Number(sp.get('p')) || 1);
  const selectedId = sp.get('id') || undefined;
  const [panel, setPanel] = useState(false);

  const path = `/api/suppliers?pageSize=${LIMIT}`;
  const { data, loading, error, reload } = useResource<Page<Supplier>>(path);
  const all = useMemo(() => data?.items || [], [data]);
  const base = useMemo(() => all.filter((v) => (!q || matches(v, q)) && (!type || (v.supplierType || '') === type)), [all, q, type]);
  const rows = useMemo(() => base.filter(view.test), [base, view]);
  const types = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of all) if (v.supplierType) m.set(v.supplierType, (m.get(v.supplierType) || 0) + 1);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'zh-CN'));
  }, [all]);

  const desk = ff === 'desktop';
  const pageRows = desk ? rows.slice((page - 1) * PAGE, page * PAGE) : rows;
  const truncated = data && data.pagination.total > all.length;

  const cols: Col<Supplier>[] = [
    { key: 'name', title: '供应商', w: 200, fix: true, render: (v) => <Two top={<b>{v.name}</b>} sub={v.code || undefined} /> },
    { key: 'type', title: '类型', w: 110, render: (v) => v.supplierType || <span className="em">—</span> },
    { key: 'contact', title: '联系人 / 电话', w: 150, render: (v) => <Two top={v.contact || <span className="em">—</span>} sub={v.phone || undefined} /> },
    { key: 'addr', title: '地址', w: 280, render: (v) => v.address || <span className="em">—</span> },
    { key: 'stl', title: '结算', w: 200, render: (v) => { const x = settlementText(v); return <Dot tone={x.dot}>{x.label}</Dot>; } },
    { key: 'st', title: '状态', w: 72, render: (v) => { const st = activeState(v.isActive); return st ? <Tag tone={st.tone} sm>{st.label}</Tag> : <span className="em">启用</span>; } },
    { key: 'note', title: '备注', w: 220, render: (v) => v.note || <span className="em">—</span> },
    { key: 'upd', title: '更新', w: 110, render: (v) => <span className="tnum em">{date(v.updatedAt)}</span> },
  ];

  const filtered = !!(q || type);
  const empty = (
    <Empty icon="store" title={filtered ? '没有符合条件的供应商' : `「${view.label}」里没有供应商`}>
      {filtered ? '换个关键词，或者清空筛选再看看。' : '换个视图看看。'}
    </Empty>
  );
  const common = { state: { loading, error, count: rows.length }, errorTitle: '供应商列表加载失败', detail: `GET ${path}`, onRetry: reload, empty };
  const bar = <><span>{rows.length} 个供应商{q && ` · 搜索「${q}」`}</span>{truncated && <span>只取了前 {LIMIT} 个</span>}</>;

  return (
    <ListScaffold
      title="客户 / 供应商"
      total={rows.length}
      segments={<Segments items={segments} />}
      chips={<ViewChips items={VIEWS.map((v) => ({ key: v.key, label: v.label, count: data ? base.filter(v.test).length : undefined }))} value={view.key} onChange={(k) => set({ view: k === 'all' ? undefined : k, id: undefined, p: undefined })} />}
      filterBtn={<FilterButton count={type ? 1 : 0} onClick={() => setPanel(true)} />}
      applied={<AppliedChips items={type ? [{ key: 'type', label: type }] : []} onRemove={() => set({ type: undefined, p: undefined })} onClear={() => set({ type: undefined, p: undefined })} />}
      search={<SearchBox value={q} placeholder="供应商名称 / 编号 / 联系人 / 电话" onSubmit={(v) => set({ q: v, id: undefined, p: undefined })} />}
      searching={!!q}
      create={can('suppliers.write') ? { href: '/suppliers/new', local: true, label: '新建供应商' } : null}
      onRefresh={reload}
      list={
        <ListBody {...common}>
          <CardList bar={bar} hasNext={false} loading={loading} count={rows.length}>
            {rows.map((v) => (
              <SupplierCard key={v.id} v={v} selected={ff === 'tablet' && v.id === selectedId} to={ff === 'tablet' ? `/suppliers?${withParam(sp, 'id', v.id)}` : `/suppliers/${v.id}`} />
            ))}
          </CardList>
        </ListBody>
      }
      desk={
        <ListBody {...common}>
          <DataTable cols={cols} rows={pageRows} onRow={(v) => nav(`/suppliers/${v.id}`)} />
        </ListBody>
      }
      pager={desk && rows.length > 0 ? <Pager page={page} size={PAGE} count={pageRows.length} total={rows.length} hasNext={page * PAGE < rows.length} onGo={(p) => set({ p: p > 1 ? String(p) : undefined })} /> : undefined}
      detail={
        ff === 'tablet'
          ? selectedId
            ? <SupplierDetail key={selectedId} id={selectedId} embedded />
            : <Empty icon="store" title="选择左侧供应商查看资料">联系方式、结算和采购记录在这里打开。</Empty>
          : undefined
      }
      overlay={panel && (
        <FilterSheet
          value={{ type }}
          groups={[{ key: 'type', title: '供应商类型', opts: types.map(([value, count]) => ({ value, label: value, count })) }]}
          onClose={() => setPanel(false)}
          onApply={(v) => { setPanel(false); set({ type: v.type, id: undefined, p: undefined }); }}
        />
      )}
    />
  );
}
